import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Product } from "@/lib/types";
import { withImageVersion } from "@/lib/imageUrl";
import { getTranslatedText } from "@/lib/translations";
import newIcon from "@/assets/classification/new.webp";
import halalIcon from "@/assets/classification/halal.webp";
import homemadeIcon from "@/assets/classification/homemade.webp";
import recommendedIcon from "@/assets/classification/recommended.png";
import vegetarianIcon from "@/assets/classification/vegetarian.png";
import spicy1Icon from "@/assets/classification/spicy1.webp";
import spicy2Icon from "@/assets/classification/spicy2.webp";
import spicy3Icon from "@/assets/classification/spicy3.webp";

const SPICY_ICONS: Record<1 | 2 | 3, string> = {
  1: spicy1Icon,
  2: spicy2Icon,
  3: spicy3Icon,
};

interface Props {
  product: Product;
  /** Cart globally frozen (during 5s submit countdown). */
  disabled: boolean;
  /** Quantité de ce produit déjà au panier (badge, comme la tablette Restaurant.js:447-451). */
  cartQty?: number;
  /** Mode consultation : pas de bouton d'ajout rapide. */
  readOnly?: boolean;
  /** Faux quand un plafond est atteint : le « + » est désactivé (Restaurant.js:465). */
  canQuickAdd?: boolean;
  /** `sourceEl` is the image container that should fly to the cart on add. */
  onOpen: (sourceEl: Element | null) => void;
  /** Ajout rapide sans ouvrir le modal (Restaurant.js:1056-1140). */
  onQuickAdd?: (sourceEl: Element | null) => void;
}

const Badges = ({ classification }: { classification?: Product["classification"] }): JSX.Element | null => {
  const { t } = useTranslation();
  if (!classification) return null;
  const items: Array<{ label: string; src: string }> = [];
  if (classification.isNew) items.push({ label: t("classification.new", "Nouveau"), src: newIcon });
  if (classification.isRecommended) items.push({ label: t("classification.recommended", "Recommandé"), src: recommendedIcon });
  if (classification.isVegetarian) items.push({ label: t("classification.vegetarian", "Végétarien"), src: vegetarianIcon });
  if (classification.isHomemade) items.push({ label: t("classification.homemade", "Maison"), src: homemadeIcon });
  if (classification.isHalal) items.push({ label: t("classification.halal", "Halal"), src: halalIcon });
  if (classification.spicyLevel && classification.spicyLevel > 0) {
    items.push({
      label: t("classification.spicy", { level: classification.spicyLevel, defaultValue: `Épicé ${classification.spicyLevel}/3` }),
      src: SPICY_ICONS[classification.spicyLevel],
    });
  }
  if (!items.length) return null;
  return (
    <div className="absolute top-3 left-3 flex flex-wrap gap-1 max-w-[70%]">
      {items.map((it) => (
        <span
          key={it.label}
          aria-label={it.label}
          title={it.label}
          className="bg-surface/95 backdrop-blur-sm rounded-full p-1 shadow-warm-sm w-7 h-7 flex items-center justify-center"
        >
          <img
            src={it.src}
            alt=""
            width={20}
            height={20}
            className="w-5 h-5 object-contain"
          />
        </span>
      ))}
    </div>
  );
};

export const ProductCard = ({
  product,
  disabled,
  cartQty = 0,
  readOnly = false,
  canQuickAdd = true,
  onOpen,
  onQuickAdd,
}: Props): JSX.Element => {
  const { t, i18n } = useTranslation();
  const [imgLoaded, setImgLoaded] = useState(false);
  const imageRef = useRef<HTMLDivElement>(null);
  const outOfOrder = product.canOrder === false;
  const blocked = disabled || outOfOrder;
  const hasImage = !!product.image?.path;
  const name = getTranslatedText(product, "name", i18n.language);
  const description = product.description ? getTranslatedText(product, "description", i18n.language) : "";
  // Tablette : le prix « 0.00 € » n'est pas affiché (Restaurant.js:418-423).
  const showPrice = (product.unitPrice ?? 0) > 0;
  const showQuickAdd = !readOnly && !outOfOrder && !!onQuickAdd;

  return (
    <article
      className="bg-surface rounded-card shadow-warm-sm overflow-hidden cursor-pointer transition-transform duration-200 active:scale-[0.985]"
      onClick={() => !blocked && onOpen(imageRef.current)}
      role="button"
      aria-label={name}
    >
      <div
        ref={imageRef}
        className="relative w-full overflow-hidden"
        style={{ aspectRatio: "4 / 3" }}
      >
        <div className="block" style={{ paddingTop: "75%" }} aria-hidden="true" />
        {hasImage ? (
          <img
            src={withImageVersion(product.image!.path!)}
            alt=""
            width={400}
            height={300}
            loading="lazy"
            decoding="async"
            onLoad={() => setImgLoaded(true)}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
              imgLoaded ? "opacity-100" : "opacity-0"
            }`}
          />
        ) : null}
        {(!hasImage || !imgLoaded) && (
          <div className="absolute inset-0 placeholder-warm flex items-center justify-center" aria-hidden="true">
            {!hasImage && (
              <span className="display-font text-4xl font-semibold text-ink/30">
                {(name || "?").trim().charAt(0).toUpperCase()}
              </span>
            )}
          </div>
        )}
        <Badges classification={product.classification} />
        {cartQty > 0 && (
          <span
            className="absolute top-3 right-3 min-w-[28px] h-7 px-2 rounded-full bg-primary text-white text-sm font-semibold flex items-center justify-center shadow-warm-sm tabular-nums"
            aria-label={`${cartQty} × ${name}`}
          >
            {cartQty}
          </span>
        )}
        {outOfOrder && (
          <div className="absolute inset-0 bg-ink/60 flex items-center justify-center">
            <span className="bg-surface text-ink rounded-pill px-4 py-2 text-sm font-medium text-center">
              {t("product.outOfOrder", "Actuellement indisponible")}
            </span>
          </div>
        )}
      </div>

      <div className="px-3 pt-3 pb-3">
        <h3 className="display-font text-base font-semibold leading-tight text-ink line-clamp-2">
          {name}
        </h3>
        {description && (
          <p className="text-xs text-ink-soft mt-1 line-clamp-2">{description}</p>
        )}
        <div className="mt-2 flex items-center justify-between gap-1 min-h-[2.25rem]">
          <span className="text-sm font-semibold tabular-nums text-ink">
            {showPrice ? `${product.unitPrice.toFixed(2)} €` : ""}
          </span>
          {showQuickAdd ? (
            <button
              type="button"
              disabled={disabled || !canQuickAdd}
              onClick={(e) => {
                e.stopPropagation();
                if (disabled || !canQuickAdd) return;
                onQuickAdd?.(imageRef.current);
              }}
              className="w-9 h-9 rounded-full bg-primary text-white text-xl leading-none font-semibold flex items-center justify-center shadow-warm disabled:opacity-40 transition-colors hover:bg-primary-hover"
              aria-label={`${t("modalProductDetail.add", "Ajouter")} ${name}`}
            >
              +
            </button>
          ) : (
            <span className="text-primary font-bold text-base leading-none" aria-hidden="true">
              ›
            </span>
          )}
        </div>
      </div>
    </article>
  );
};
